import { useEffect, useRef, useCallback } from 'react';
import { useLogin } from '../context/LoginContext';
import logger from '../utils/logger';

const SETTINGS_STORAGE_KEY = 'ti_app_settings';

// A throttle function to limit how often a function can be called.
const throttle = (func, limit) => {
  let inThrottle = false;
  let trailingCall = false;
  let lastArgs = null;
  let lastContext = null;

  return function() {
    lastContext = this;
    lastArgs = arguments;

    if (!inThrottle) {
      inThrottle = true;
      func.apply(lastContext, lastArgs);

      setTimeout(() => {
        inThrottle = false;
        if (trailingCall) {
          func.apply(lastContext, lastArgs);
          trailingCall = false;
        }
      }, limit);
    } else {
      // A call came in during the throttle period.
      // Mark that we should make one more call after the period ends.
      trailingCall = true;
    }
  };
};

const useAutoLogout = () => {
  const { logout, isAuthenticated } = useLogin();
  const timerId = useRef(null);

  const handleLogout = useCallback(async () => {
    logger.info('Auto-logout due to inactivity.');
    try {
      await logout('inactivity');
    } catch (err) {
      logger.error('Auto-logout failed:', err);
    }
  }, [logout]);

  const startTimers = useCallback(() => {
    let autoLogoutMinutes = 0;
    try {
      const settings = JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) || '{}');
      autoLogoutMinutes = parseInt(settings.autoLogoutTimer || '0', 10);
    } catch (e) {
      logger.error('Could not parse settings for auto-logout timer', e);
    }

    // If timer is disabled (set to 0), do nothing.
    if (autoLogoutMinutes <= 0) {
      return;
    }

    const timeoutDuration = autoLogoutMinutes * 60 * 1000;

    // Set the main logout timer
    timerId.current = setTimeout(handleLogout, timeoutDuration);
  }, [handleLogout]);

  const resetTimers = useCallback(() => {
    if (timerId.current) clearTimeout(timerId.current);
    startTimers();
  }, [startTimers]);

  // Effect to set up and clean up event listeners
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }


    // Throttle the reset function to avoid performance issues with frequent events like 'mousemove'.
    // We'll reset the timer at most once every 5 seconds.
    const throttledReset = throttle(resetTimers, 5000);

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];

    // Listen for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, throttledReset);
    });

    const handleStorageChange = (e) => {
      if (e.key === SETTINGS_STORAGE_KEY) {
        resetTimers();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    const handleSettingsChange = () => {
        resetTimers();
    };
    window.addEventListener('settings-changed', handleSettingsChange);

    // Start timers on initial load
    startTimers();

    // Cleanup function
    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, throttledReset));
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settings-changed', handleSettingsChange);
      if (timerId.current) clearTimeout(timerId.current);
    };
  }, [isAuthenticated, resetTimers, startTimers]);
};

export default useAutoLogout;